/**
 * @file customer-support.component.ts
 */
import { Component, OnInit } from '@angular/core';
import { MultilingualService } from '@core/services';
import { CustomerSupport, CUSTOMER_SUPPORT_ENUM } from '@models/interfaces';
import { PhoneNumberPipe } from '@shared/pipes';
import {
  AppCenterAnalytics,
  AppInsightsService,
  ClientsService,
  CmsService,
  UserService,
} from '@shared/services';
import { isOnlyDigits } from '@shared/utils';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-customer-support',
  templateUrl: './customer-support.component.html',
  styleUrls: ['./customer-support.component.scss'],
  standalone: false,
})
export class CustomerSupportComponent implements OnInit {
  customerSupportItems$!: Observable<CustomerSupport[]>;
  errorMsg!: string;
  imageMap: Record<CUSTOMER_SUPPORT_ENUM, string> = {
    LABEL_COMMON_MEMBER_SERVICES: 'customer-support-member-service.svg',
    LABEL_COMMON_TECHNICAL_SUPPORT: 'customer-support-tech.svg',
    LABEL_CUSTOMER_SUPPORT_MAIL_ORDER: 'customer-support-mail-order.svg',
  };

  get mailOrderPharmacyNameFn(): Observable<{ pharmacyName: string }> {
    return this.clientService.getMailOrderPharmacyDetails().pipe(
      map((pharmacy) => {
        return pharmacy ? { pharmacyName: pharmacy.name } : { pharmacyName: '' };
      })
    );
  }

  constructor(
    private readonly appInsightsService: AppInsightsService,
    public readonly multilingualService: MultilingualService,
    public readonly cmsService: CmsService,
    public clientService: ClientsService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.appInsightsService.trackPageView(AppCenterAnalytics.CUSTOMER_SUPPORT_VIEWED);
    this.getCustomerSupport();
  }

  getCustomerSupport(): void {
    const clientId = this.userService.eligibilityVerificationResponse?.clientId ?? '';

    this.customerSupportItems$ = this.cmsService.getCustomerSupport().pipe(
      map((res) => {
        const customerSupportItems = (res || []).map((item) => {
          const customerSupportItem: CustomerSupport = {
            details: item,
            imageUrl: this.imageMap[item.key] || this.imageMap.LABEL_COMMON_TECHNICAL_SUPPORT,
            title: item.key,
            hide$: of(false),
          };
          return customerSupportItem;
        });
        return customerSupportItems;
      }),
      switchMap((customerSupportItems) =>
        this.addMemberServiceSupportDetails(customerSupportItems, clientId)
      ),
      switchMap((customerSupportItems) => this.addMailOrderPharmacyDetails(customerSupportItems)),
      catchError((error) => {
        this.errorMsg = error;
        return of([]);
      })
    );
  }

  private addMemberServiceSupportDetails(
    customerSupportItems: CustomerSupport[],
    clientId: string
  ) {
    // Get Member service number for client
    return this.getMemberServiceNumber(clientId).pipe(
      map((memberServiceNumber: string) => {
        const memberServiceSupportItem = customerSupportItems.find(
          (x) => x.details.key === CUSTOMER_SUPPORT_ENUM.LABEL_COMMON_MEMBER_SERVICES
        );
        if (memberServiceNumber && memberServiceSupportItem) {
          memberServiceSupportItem.details.supportNumber = memberServiceNumber;

          // if the phone number is valid 10 digit number, then format the phone number
          if (isOnlyDigits(memberServiceNumber)) {
            const formatNumber = new PhoneNumberPipe();
            memberServiceSupportItem.details.supportNumber =
              formatNumber.transform(memberServiceNumber);
          }
        }
        return customerSupportItems;
      }),
      catchError(() => of(customerSupportItems))
    );
  }

  private addMailOrderPharmacyDetails(customerSupportItems: CustomerSupport[]) {
    const pharmacySupportItem = customerSupportItems.find(
      (x) => x.details.key === CUSTOMER_SUPPORT_ENUM.LABEL_CUSTOMER_SUPPORT_MAIL_ORDER
    );

    // Get Mail order pharmacy support number
    return this.clientService.getMailOrderPharmacyDetails().pipe(
      map((pharmacyDetails) => {
        if (pharmacyDetails) {
          const pharmacySupportNumber = pharmacyDetails?.supportNumber;

          if (pharmacySupportNumber && pharmacySupportItem) {
            pharmacySupportItem.details.supportNumber = pharmacySupportNumber;

            // if the phone number is valid 10 digit number, then format the phone number
            if (isOnlyDigits(pharmacySupportNumber)) {
              const formatNumber = new PhoneNumberPipe();
              pharmacySupportItem.details.supportNumber =
                formatNumber.transform(pharmacySupportNumber);
            }
          }
        } else if (pharmacySupportItem) {
          pharmacySupportItem.hide$ = of(true);
        }

        return customerSupportItems;
      }),
      catchError(() => of(customerSupportItems))
    );
  }

  private getMemberServiceNumber(clientId: string): Observable<string> {
    return this.clientService.fetchMemberServicesNumber(clientId);
  }
}
